const preLoginLightPalette = {
    alternate: { main: '#f3f1f5ff', dark: '#fbeff0' },
    mode: 'light',
    primary: {
        light: '#ede7f6',
        main: '#5931c2',
        dark: '#443981',
        main70Opacity: 'rgba(89, 49, 194, 0.7)',
        main50Opacity: 'rgba(89, 49, 194, 0.5)',
        main30Opacity: 'rgba(89, 49, 194, 0.3)',
        main20Opacity: 'rgba(89, 49, 194, 0.2)',
    },
    secondary: {
        light: '#f7e32a',
        main: '#f5b840',
        dark: '#ab802c',
        mainOrange: '#D4670C',
        mainRed: '#EF5C29',
        mainGreen: '#839D5E',
        mainSalmon: '#f4cfb4',
        mainBrown: '#3c2116',
        mainGray: '#70656d',
    },
    text: {
        primaryLight: '#634d44',
        primary: '#3c2116',
        primaryDark: '#2a170f',
        secondaryLight: '#8c838a',
        secondary: '#70656d',
        secondaryDark: '#4e464c',
        headerLight: '#fbeff0',
        bodyLight: '#ede7f6',
        btnPrimaryCTA: '#FBEFF0',
        btnSecondaryCTA: '#5931C2',
        btnPrimaryInfo: '#3c2116',
    },
    divider: {
        light: 'rgba(0, 0, 0, 0.12)',
        main: 'rgba(105, 105, 105, 0.5)',
        dark: 'rgba(250, 239, 224, 1)',
    },
    background: {
        default: 'rgba(253, 247, 247, 1)',
        paper: 'rgba(253, 247, 247, 1)',
        paperLight: 'rgba(251, 239, 240, 0.5)',
        paperDark: '#fbeff0',
        transparentDefault: 'rgba(246,243,250,1)',
        btnPrimaryCTA: '#5931C2',
        btnSecondaryCTA: '#F5B840',
        btnPrimaryInfo: '#F4CFB4',
        footerDark: '#3c2116',
    },
    gray: { light: '#8c838a', main: '#70656d', dark: '#4e464c' },
    brown: { light: '#634d44', main: '#3c2116', dark: '#2a170f' },
    warning: { light: '#f7e32a', main: '#f5b840', dark: '#ab802c' },
    orange: { light: '#ff955a', main: '#d4670c', dark: '#ef5c29' },
    error: { light: '#d4670c', main: '#ef5c29', dark: '#f45620' },
    success: { light: '#cef565', main: '#33bf8a', dark: '#839d5e' },
};

const preLoginDarkPalette = {
    alternate: { main: '#1a2138', dark: '#151a30' },
    common: { black: '#000', white: '#fff' },
    mode: 'dark',
    primary: { main: '#1976d2', light: '#2196f3', dark: '#0d47a1' },
    secondary: { light: '#FFEA41', main: '#FFE102', dark: '#DBBE01' },
    text: { primary: '#EEEEEF', secondary: '#AEB0B4' },
    divider: 'rgba(255, 255, 255, 0.12)',
    background: { paper: '#222B45', default: '#222B45' },
};

/**
 * Returns the correct palette object based on the provided mode.
 * @param {object} config - The configuration object.
 * @param {'preLogin' | 'postLogin'} config.mode - The primary theme mode.
 * @param {'light' | 'dark'} config.colorMode - The color mode for the preLogin theme.
 * @param {object} config.themeOption - The blueprint for the postLogin theme.
 * @returns A complete MUI palette object.
 */
const getPalette = ({ themeOption, mode, colorMode }) => {
    switch (mode) {
        case 'preLogin':
            // If the mode is 'preLogin', we return one of the static palettes
            // based on the requested colorMode ('light' or 'dark').
            return colorMode === 'light' ? preLoginLightPalette : preLoginDarkPalette;

        case 'postLogin':
        default: {
            // If the mode is 'postLogin', we dynamically build the palette
            // using the `themeOption` blueprint, just like the original file did.
            const colors = themeOption.colors;
            return {
                mode: themeOption.customization.navType, // Can be 'light' or 'dark'
                common: {
                    black: '#000000',
                    white: '#ffffff',
                },
                primary: {
                    light: colors.primaryLight,
                    main: colors.primaryMain,
                    dark: colors.primaryDark,
                    200: colors.primary200,
                    800: colors.primary800,
                },
                secondary: {
                    light: colors.secondaryLight,
                    main: colors.secondaryMain,
                    dark: colors.secondaryDark,
                    200: colors.secondary200,
                    800: colors.secondary800,
                },
                error: {
                    light: colors.errorLight,
                    main: colors.errorMain,
                    dark: colors.errorDark,
                },
                orange: {
                    light: colors.orangeLight,
                    main: colors.orangeMain,
                    dark: colors.orangeDark,
                },
                warning: {
                    light: colors.warningLight,
                    main: colors.warningMain,
                    dark: colors.warningDark,
                },
                success: {
                    light: colors.successLight,
                    200: colors.success200,
                    main: colors.successMain,
                    dark: colors.successDark,
                },
                grey: {
                    50: colors.grey50,
                    100: colors.grey100,
                    500: themeOption.darkTextSecondary,
                    600: themeOption.heading,
                    700: themeOption.darkTextPrimary,
                    900: themeOption.textDark,
                },
                text: {
                    primary: themeOption.darkTextPrimary,
                    secondary: themeOption.darkTextSecondary,
                    dark: themeOption.textDark,
                    hint: colors.grey100,
                },
                background: {
                    paper: themeOption.paper,
                    default: themeOption.backgroundDefault,
                },
            };
        }
    }
};

export default getPalette;